import { useState } from "react";
import { motion } from "framer-motion";
import { Home, CheckCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { externalSupabase } from "@/lib/externalSupabase";

const HostOnboarding = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    propertyName: "",
    airbnbLink: "",
    directUrl: "",
    hostEmail: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.propertyName.trim() || !form.airbnbLink.trim() || !form.directUrl.trim() || !form.hostEmail.trim()) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }

    if (!/^https?:\/\/.+/.test(form.airbnbLink.trim())) {
      toast({ title: "Please enter a valid Airbnb URL", variant: "destructive" });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(form.hostEmail.trim())) {
      toast({ title: "Please enter a valid email", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // Upsert host
      const { data: hostData, error: hostError } = await externalSupabase
        .from("hosts")
        .upsert(
          {
            owner_email: form.hostEmail.trim(),
            website_url: form.directUrl.trim(),
            owner_name: form.propertyName.trim(),
          } as any,
          { onConflict: "owner_email" }
        )
        .select("id")
        .single();

      if (hostError) throw hostError;

      // Upsert property
      const { error: propError } = await externalSupabase
        .from("properties")
        .upsert(
          {
            airbnb_url: form.airbnbLink.trim(),
            original_name: form.propertyName.trim(),
            is_verified: true,
            host_id: hostData?.id || null,
            finding_method: "host_onboarding",
          } as any,
          { onConflict: "airbnb_url" }
        );

      if (propError) throw propError;

      setSubmitted(true);
      toast({ title: "Property submitted successfully!" });
    } catch (err: any) {
      console.error("Host onboarding error:", err);
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
              <Home className="w-7 h-7 text-accent-foreground" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
              List Your Property on Bnbxo
            </h1>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              Get verified instantly. Let guests find your direct booking site and skip the platform fees.
            </p>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-border rounded-2xl p-10 text-center"
            >
              <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="font-display text-2xl font-bold text-foreground mb-2">You're all set!</h2>
              <p className="text-muted-foreground">
                Your property has been verified. Guests searching for your listing will now see your direct booking link.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              onSubmit={handleSubmit}
              className="bg-card border border-border rounded-2xl p-8 space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Property Name</label>
                <Input
                  name="propertyName"
                  placeholder="e.g. Sunset Villa Tulum"
                  value={form.propertyName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Airbnb Link</label>
                <Input
                  name="airbnbLink"
                  placeholder="https://airbnb.com/rooms/..."
                  value={form.airbnbLink}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Direct Booking URL</label>
                <Input
                  name="directUrl"
                  placeholder="https://yourdirectsite.com"
                  value={form.directUrl}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Host Email</label>
                <Input
                  name="hostEmail"
                  type="email"
                  placeholder="you@example.com"
                  value={form.hostEmail}
                  onChange={handleChange}
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {submitting ? "Submitting..." : "Verify My Property"}
              </Button>
            </motion.form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HostOnboarding;
